import {
  AccessRights,
  UserRole
} from 'src/app/features/role/user-role/user-role'
import { Component, OnInit } from '@angular/core'

import { Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { Actions, ofType } from '@ngrx/effects'
import { combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { BsModalService } from 'ngx-bootstrap/modal'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import moment from 'moment-mini'

import { Organization } from '../organization'
import { organizationSelectors } from '../organization.selectors'
import { userRoleSelectors } from '../../role/user-role/user-role.selectors'
import { roleActions } from '../../role/role.actions'
import { BulkAction, BulkActionEvent } from '../../../shared/interfaces'
import { userRoleActions } from '../../role/user-role/user-role.actions'
import { NotificationService } from '../../../core/notification.service'
import { AppState } from '../../../core/core.state'
import { UserRoleShowModalComponent } from '../../role/user-role/show-modal/show.component'
import { UserRoleCreateModalComponent } from '../../role/user-role/create-modal/show.component'
import { roleSelectors } from '../../role/role.selectors'
import { memberSelectors } from '../../member/member.selectors'
import { Subscriber } from '../../subscription/subscriber/subscriber'
import { subscriberSelectors } from '../../subscription/subscriber/subscriber.selectors'
import { SubscriberService } from '../../subscription/subscriber/subscriber.service'
import { organizationActions } from '../organization.actions'
import { DownloadFileService } from '../../../shared/services/download-file.service'
import { OrganizationService } from '../organization.service'
import { ActivatedRoute } from '@angular/router'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'

@UntilDestroy()
@Component({
  selector: 'organization-user-role-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class OrganizationUserRolesTabComponent implements OnInit {
  public organization$: Observable<Organization>
  public selectedOrganization: Organization
  public organizationId$: Observable<number>
  public userRoles$: Observable<UserRole[]>
  public accessRights = AccessRights

  public roleEntities$ = this.store.select(roleSelectors.selectEntities)
  public memberEntities$ = this.store.select(memberSelectors.selectEntities)

  public BulkAction = BulkAction

  public subscribers: Subscriber[]

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private notificationService: NotificationService,
    private modalService: BsModalService,
    private translate: TranslateService,
    private downloadFileService: DownloadFileService,
    private subscriberService: SubscriberService,
    private organizationService: OrganizationService,
    private activeRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.downloadFileService.initialize()

    this.store.dispatch(roleActions.fetchMany({}))
    this.store
      .select(subscriberSelectors.selectAll)
      .pipe(untilDestroyed(this))
      .subscribe((subscribers) => {
        this.subscribers = subscribers
      })
    if (this.organizationService.showModal) {
      this.activeRoute.params.pipe(take(1)).subscribe((params) => {
        if (params.hasOwnProperty('organizationId')) {
          this.organizationId$ = of(Number(params['organizationId']))
          this.store.dispatch(
            userRoleActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { organizationId: Number(params['organizationId']) },
                join: [{ field: 'member' }]
              })
            })
          )
        }
      })
    } else {
      this.organizationId$ = this.store
        .select(organizationSelectors.selectIdRouteParam)
        .pipe(
          filter((id) => Boolean(id)),
          tap((id) =>
            this.store.dispatch(
              userRoleActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: { organizationId: id },
                  join: [{ field: 'member' }]
                })
              })
            )
          )
        )
    }

    this.organizationId$.pipe(take(1)).subscribe((organizationId) =>
      this.store.dispatch(
        organizationActions.fetchOneById({
          id: organizationId,
          params: RequestQueryBuilder.create({
            join: [
              { field: 'userRoles' },
              { field: 'userRoles.organization' },
              { field: 'userRoles.member' },
              { field: 'userRoles.member.role' },
              { field: 'userRoles.member.subscribers' },
              { field: 'requiredSubscriptions' }
            ]
          })
        })
      )
    )

    this.organization$ = this.organizationId$.pipe(
      switchMap((organizationId) =>
        this.store.select(organizationSelectors.selectById, organizationId)
      )
    )

    this.organization$.pipe(untilDestroyed(this)).subscribe((organization) => {
      this.selectedOrganization = organization
    })
    this.userRoles$ = this.organizationId$.pipe(
      switchMap((organizationId) =>
        this.store.select(userRoleSelectors.selectByFilter, {
          field: 'organizationId',
          value: organizationId
        })
      )
    )
  }

  public onClickRow(userRole: UserRole) {
    this.modalService.show(UserRoleShowModalComponent, {
      initialState: {
        title: 'roleModule.role',
        id: userRole.id
      },
      class: 'modal-lg modal-dialog-scrollable'
    })
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent) {
    switch (bulkActionEvent.action) {
      case BulkAction.DETACH:
        bulkActionEvent.items.map((item) =>
          this.store.dispatch(userRoleActions.deleteOne({ id: item.id }))
        )
        break
      case BulkAction.EXPORT:
        this.downloadCSV(bulkActionEvent.items)
        return
      case BulkAction.APPROVE:
        bulkActionEvent.items.forEach((userRole) => {
          this.store.dispatch(
            userRoleActions.updateOneById({
              id: userRole.id,
              params: { isApproved: true }
            })
          )
        })
        break
    }

    this.actions$
      .pipe(
        ofType(userRoleActions.removeOne),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )
  }

  public onClickCreateUserRole() {
    this.organizationId$.pipe(take(1)).subscribe((organizationId) => {
      this.modalService.show(UserRoleCreateModalComponent, {
        initialState: {
          title: 'general.create',
          userRole: { organizationId: parseInt(organizationId.toString()) },
          createRole: true,
          lockOrganization: true
        },
        class: 'modal-lg modal-dialog-scrollable'
      })
    })
  }

  public downloadCSV(rows: UserRole[]) {
    combineLatest([this.roleEntities$, this.memberEntities$])
      .pipe(
        take(1),
        withLatestFrom(
          this.translate.get('memberModule.role'),
          this.translate.get('general.name'),
          this.translate.get('memberModule.memberId'),
          this.translate.get('general.status'),
          this.translate.get('subscriptionModule.subscription'),
          this.translate.get('general.approved'),
          this.translate.get('general.active'),
          this.translate.get('general.pending')
        )
      )
      .subscribe(
        ([
          [roles, members],
          roleTranslation,
          nameTranslation,
          memberIdTranslation,
          statusTranslation,
          subscriptionTranslation,
          ApprovedTranslation,
          ActiveTranslation,
          PendingTranslation
        ]) => {
          const header = [
            roleTranslation,
            nameTranslation,
            memberIdTranslation,
            statusTranslation,
            subscriptionTranslation
          ]

          const mappedRows = rows.map((row) => {
            return [
              roles[row.roleId]?.description,
              `${members[row.userId]?.firstName} ${
                members[row.userId]?.lastName
              }`,
              members[row.userId]?.memberNumber,
              row.isApproved ? ApprovedTranslation : PendingTranslation,
              this.memberHasRequiredSubscriptions(row.userId)
                ? ActiveTranslation
                : ' '
            ]
          })
          const csvRows = [header, ...mappedRows]
          this.store
            .select(settingSelectors.selectById, 'language')
            .pipe(take(1))
            .subscribe((language) => {
              this.downloadFileService.downloadFile(
                'csv',
                'members',
                csvRows,
                language
              )
            })
        }
      )
  }

  public memberHasRequiredSubscriptions(memberId: number): boolean {
    return this.subscriberService.hasActiveSubscription(
      this.subscribers.filter((s) => s.memberId === memberId),
      this.selectedOrganization?.requiredSubscriptions ?? [],
      moment()
    )
  }
}
